import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  ClipboardCheck,
  LineChart,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import {
  buildAgeChartModel,
  buildAgeExecutiveMarkdown,
  buildExecutiveKpis,
  buildGenderBars,
  buildGirlsGaugeStyle,
  buildHeroNarrative,
  buildManagementDonut,
  buildPrimaryExecutiveAction,
  buildPriorityCards,
  buildRiskWatchlist,
  buildStageLens,
  buildWorkflowActions,
  fmt,
  formatLakh,
} from "@/utils/ageEnrolmentDerived";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./AgeEnrolmentExecutiveTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const PRIORITY_ICONS = {
  "": Users,
  pink: User,
  orange: LineChart,
};

const WORKFLOW_ICONS = {
  pink: User,
  orange: LineChart,
  "": ClipboardCheck,
};

const AgeEnrolmentExecutiveTab = ({ overview = {}, ageData = [], managementData = [], blockData = [] }) => {
  const girlsPct = overview.girls_pct ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const hasData = Boolean(overview?.total_enrolment);

  const buildLocalMarkdown = useCallback(
    () => buildAgeExecutiveMarkdown(overview, []),
    [overview]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ e: overview?.total_enrolment, g: overview?.gender_parity_index }),
    [overview?.total_enrolment, overview?.gender_parity_index]
  );

  const { loading: aiLoading, pickFromBucket, pickPrimaryAction } = useMistralNarrativeOverlay({
    kind: "age-enrolment-executive",
    title: "Age Enrolment Executive Summary",
    buildLocalMarkdown,
    context: { overview, ageData, managementData },
    enabled: hasData,
    scopeKey,
  });

  const gaugeStyle = useMemo(() => buildGirlsGaugeStyle(girlsPct), [girlsPct]);
  const localNarrative = useMemo(() => buildHeroNarrative(overview), [overview]);
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 0)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const localPrimaryAction = useMemo(() => buildPrimaryExecutiveAction(overview), [overview]);
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const kpis = useMemo(() => buildExecutiveKpis(overview), [overview]);
  const genderBars = useMemo(() => buildGenderBars(overview), [overview]);
  const chart = useMemo(() => buildAgeChartModel(ageData, overview), [ageData, overview]);
  const management = useMemo(() => buildManagementDonut(managementData, overview), [managementData, overview]);
  const stageLens = useMemo(() => buildStageLens(overview), [overview]);
  const priorityCards = useMemo(() => buildPriorityCards(overview), [overview]);
  const watchlist = useMemo(() => buildRiskWatchlist(overview), [overview]);
  const workflow = useMemo(() => buildWorkflowActions(overview, blockData), [overview, blockData]);

  const gpiLabel = gpi < 0.97 ? "Girls lag" : gpi > 1.03 ? "Boys lag" : "Near parity";

  return (
    <div className="aew-exec-tab" data-testid="age-enrolment-executive">
      <section className="aew-hero-card" id="aew-exec-hero">
        <div className="aew-hero-layout">
          <div className="aew-enrolment-main">
            <div className="aew-headline-card">
              <div
                className="aew-student-gauge"
                style={gaugeStyle}
                aria-label={`Girls participation ${girlsPct} percent`}
              >
                <div>
                  <strong>{girlsPct}%</strong>
                  <span>Girls share</span>
                </div>
              </div>
              <div className="aew-hero-copy">
                <h2>{formatLakh(overview.total_enrolment)} students enrolled</h2>
                <p>{narrative}</p>
                <div className="aew-status-row">
                  <HeroStatusChip
                    chipClass="aew-status-chip"
                    onClick={gpi < 0.95 || gpi > 1.05 ? () => scrollToId("aew-priority-actions") : undefined}
                    ariaLabel={
                      gpi < 0.95 || gpi > 1.05
                        ? `GPI ${gpi} · ${gpiLabel} — view priority actions`
                        : undefined
                    }
                  >
                    GPI {gpi} · {gpiLabel}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="aew-status-chip"
                    variant="blue"
                    onClick={() => scrollToId("aew-age-curve")}
                    ariaLabel={`Peak age ${overview.peak_enrolment_age} yrs — view age enrolment curve`}
                  >
                    Peak age {overview.peak_enrolment_age} yrs
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="aew-status-chip" variant="green" showChevron={false}>
                    {fmt(overview.total_schools)} schools covered
                  </HeroStatusChip>
                </div>
              </div>
            </div>

            <div className="aew-summary-note">
              <div className="aew-icon-box pink">
                <UserPlus size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>

            <div className="aew-split-card">
              <div className="aew-split-head">
                <div>
                  <h3>Gender Composition</h3>
                  <p>Clear split view replaces scattered boy/girl KPI cards.</p>
                </div>
                <span className="aew-pill">Parity focus</span>
              </div>
              <div className="aew-gender-bars">
                {genderBars.map((row) => (
                  <div key={row.label} className="aew-gender-row">
                    <span>{row.label}</span>
                    <div className="aew-gender-track">
                      <div
                        className={`aew-gender-fill ${row.barClass}`.trim()}
                        style={{ width: `${row.widthPct}%` }}
                      />
                    </div>
                    <div className="aew-gender-value">{fmt(row.value)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="aew-metric-grid" aria-label="Executive enrolment KPIs">
            {kpis.map((card) => (
              <article key={card.label} className={`aew-metric-card ${card.cardClass}`.trim()}>
                <small>{card.label}</small>
                <strong>{card.value}</strong>
                <span>{card.sub}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="aew-section-title" id="aew-age-curve">
        <div>
          <h2>Age Curve and Management Mix</h2>
          <p>
            The age trend is given full width for readability, while management composition is grouped
            with stage-level enrolment signals for faster interpretation.
          </p>
        </div>
        <button type="button" className="aew-section-action" onClick={() => scrollToId("aew-priority-actions")}>
          View recommended actions →
        </button>
      </div>

      <section className="aew-analytics-grid">
        <article className="aew-panel">
          <div className="aew-panel-head">
            <div>
              <h3>Age-Wise Enrolment Trend</h3>
              <p>Distribution across ages 4–18. The sharp post-16 decline is visually highlighted.</p>
            </div>
            <span className="aew-pill">Students by age</span>
          </div>
          <div className="aew-age-chart" role="img" aria-label="Area chart showing enrolment by age from 4 to 18 years">
            <svg viewBox={`0 0 ${chart.width} ${chart.height}`}>
              <defs>
                <linearGradient id="aewAgeFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity="0.34" />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity="0.05" />
                </linearGradient>
                <filter id="aewSoftShadow" x="-10%" y="-10%" width="120%" height="120%">
                  <feDropShadow dx="0" dy="8" stdDeviation="8" floodColor="#1f4a8b" floodOpacity="0.12" />
                </filter>
              </defs>
              <line x1={chart.left} y1="40" x2={chart.left} y2={chart.bottom} className="aew-axis" />
              <line x1={chart.left} y1={chart.bottom} x2={chart.width - 40} y2={chart.bottom} className="aew-axis" />
              {chart.yTicks.map((tick) => (
                <g key={tick.label}>
                  <line x1={chart.left} y1={tick.y} x2={chart.width - 40} y2={tick.y} className="aew-grid-line" />
                  <text x="42" y={tick.y + 4} className="aew-axis-label">
                    {tick.label}
                  </text>
                </g>
              ))}
              <path d={chart.areaPath} fill="url(#aewAgeFill)" />
              <path
                d={chart.linePath}
                fill="none"
                stroke="#2563eb"
                strokeWidth="3"
                filter="url(#aewSoftShadow)"
              />
              <circle cx={chart.peakPoint.x} cy={chart.peakPoint.y} r="5" fill="#2563eb" />
              <text x={chart.peakPoint.x + 18} y={chart.peakPoint.y - 5} className="aew-peak-label">
                Peak: age {chart.peakAge}
              </text>
              <path
                d={chart.declinePath}
                fill="none"
                stroke="#ef4444"
                strokeWidth="3"
                strokeDasharray="7 7"
              />
              <text x={chart.peakPoint.x + 118} y={chart.peakPoint.y + 33} className="aew-chart-label">
                Secondary decline zone
              </text>
              <g className="aew-axis-label">
                {chart.points.map((p) => (
                  <text key={p.age} x={p.x - 3} y={chart.bottom + 23}>
                    {p.age}
                  </text>
                ))}
              </g>
            </svg>
          </div>
        </article>

        <article className="aew-panel">
          <div className="aew-panel-head">
            <div>
              <h3>School Management Composition</h3>
              <p>Grouped view for the top management types and school-stage reading.</p>
            </div>
            <span className="aew-pill">Top 6</span>
          </div>
          <div className="aew-insight-stack">
            <div className="aew-mini-panel">
              <h4>Management mix</h4>
              <p>Local body and private unaided institutions form the largest share in the selected district scope.</p>
              <div className="aew-management-wrap">
                <div className="aew-donut" style={management.style} aria-hidden="true" />
                <div className="aew-legend">
                  {management.legend.map((row) => (
                    <div key={row.fullName} className="aew-legend-row">
                      <span>
                        <i className="aew-dot" style={{ background: row.color }} />
                        {row.name}
                      </span>
                      <b>{row.pct}%</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="aew-mini-panel">
              <h4>Stage transition lens</h4>
              <p>Use this as the executive funnel from early years to secondary-age retention.</p>
              <div className="aew-stage-flow">
                {stageLens.map((item) => (
                  <div key={item.label} className="aew-stage-card">
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </section>

      <div className="aew-section-title" id="aew-priority-actions">
        <div>
          <h2>Executive Priority Actions</h2>
          <p>
            The bottom summary cards have been converted into action-led cards so decision makers know
            exactly what to investigate next.
          </p>
        </div>
        <button type="button" className="aew-section-action" onClick={() => scrollToId("aew-exec-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="aew-priority-grid">
        {priorityCards.map((card) => {
          const Icon = PRIORITY_ICONS[card.iconClass] ?? Users;
          return (
            <article key={card.id} className="aew-priority-card">
              <div className="aew-priority-head">
                <div className={`aew-icon-box ${card.iconClass}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`aew-priority-tag ${card.tagClass}`.trim()}>{card.tag}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="aew-priority-metric">
                <strong>{card.metric}</strong>
                <span>{card.metricLabel}</span>
              </div>
            </article>
          );
        })}
      </section>

      <section className="aew-two-column">
        <article className="aew-panel aew-table-card">
          <div className="aew-table-head">
            <div>
              <h3>Risk Watchlist Design</h3>
              <p>Suggested table for the next implementation step.</p>
            </div>
            <span className="aew-pill">Actionable view</span>
          </div>
          <table className="aew-risk-table" aria-label="Risk watchlist design">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Why it matters</th>
                <th>Primary owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.map((row) => (
                <tr key={row.signal}>
                  <td className="score">{row.signal}</td>
                  <td>{row.why}</td>
                  <td>{row.owner}</td>
                  <td>
                    <span className={`aew-tag ${row.tagClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="aew-panel">
          <div className="aew-panel-head">
            <div>
              <h3>Recommended Workflow</h3>
              <p>Three steps for turning the dashboard into weekly action.</p>
            </div>
            <span className="aew-pill">Next review</span>
          </div>
          <div className="aew-action-list">
            {workflow.map((item) => {
              const Icon = WORKFLOW_ICONS[item.iconClass] ?? ClipboardCheck;
              return (
                <div key={item.title} className="aew-action-item">
                  <div className={`aew-icon-box ${item.iconClass}`.trim()}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>
      </section>
    </div>
  );
};

export default AgeEnrolmentExecutiveTab;
